import React, { useContext, useState } from 'react';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { CreditApprovalRound } from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { DecisionModal } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/DecisionModal/DecisionModal';
import {
  getCreditApprovalData,
  resetCreditApproval as resetCreditApprovalMutation
} from 'features/brazil-grower/queries/creditApproval.queries';
import { UserContext } from 'core/components/GateWay/GateWay';
import { financingAgFinancing } from 'features/brazil-grower/queries';

interface ResetCreditApprovalProps {
  round?: CreditApprovalRound;
}

export const ResetCreditApproval: React.FunctionComponent<ResetCreditApprovalProps> = ({ round }) => {
  /**
   * State
   */
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);

  /**
   * Mutations
   */
  const [resetCreditApprovalFn] = useMutation(resetCreditApprovalMutation);

  /**
   * Context
   */
  const { agFinancingId } = useContext(FinanceProcessContext);
  const { isCreditAnalyst } = useContext(UserContext);

  const { addFlashMessage } = useFlashMessage();

  if (!round || !isCreditAnalyst) {
    return null;
  }

  const resetCreditApproval = (resetNotes: string) => {
    if (!round) {
      return Promise.reject('no credit approval process to reset');
    }

    return resetCreditApprovalFn({
      variables: {
        agFinancingId,
        notes: resetNotes
      },
      refetchQueries: [
        {
          // * Refetch credit approval data so the user gets the live data on the decision sheet
          query: getCreditApprovalData,
          variables: { agFinancingId }
        },
        {
          // * Refetch negotiation details to show the correct view in the credit approval section
          query: financingAgFinancing,
          variables: { agFinancingId }
        }
      ],
      awaitRefetchQueries: true
    })
      .then(() => {
        setIsDecisionOpen(false);
        addFlashMessage({
          content: '',
          title: i18next.t('creditApprovalSuccessfullyReset'),
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  return (
    <>
      <DecisionModal
        title={i18next.t('reset')}
        onConfirmAction={resetCreditApproval}
        open={isDecisionOpen}
        closeModal={() => setIsDecisionOpen(false)}
        notesRequired
      />
      <Button
        variant="secondary"
        size="small"
        onClick={() => setIsDecisionOpen(true)}
        className="text-transform-capitalize"
      >
        {i18next.t('reset')}
      </Button>
    </>
  );
};
